/**
 * 캠페인 화면의 **기간 필터** — 대상은 캠페인이 아니라 **게시글**이다.
 * 게시글은 날짜가 하나(게시일)라 「시작~종료 안에 있다/없다」가 분명하다. 걸러진 게시글로
 * 캠페인 줄·그룹 줄·카드·상세 표·엑셀이 한 줄기로 다시 센다.
 *
 * 필터의 시작·끝은 날짜 단위다: 시작은 그날 0시, 끝은 그날 23:59:59.999. 한쪽만 있으면 그쪽만 본다.
 * 둘 다 없으면 필터가 없는 것이다. 게시일을 못 읽는 글은 필터가 걸려 있으면 **빠진다**.
 */

export type CampaignPeriodFilter = {
  start: Date | null;
  end: Date | null;
};

export const EMPTY_CAMPAIGN_PERIOD_FILTER: CampaignPeriodFilter = { start: null, end: null };

/** 필터가 걸려 있는가 — 둘 다 비면 false */
export function isCampaignPeriodFilterActive(filter: CampaignPeriodFilter | null | undefined): boolean {
  return Boolean(filter?.start || filter?.end);
}

/**
 * 기간 칩의 말 — 비면 「전체」, 둘이면 「09.01~09.07」, 한쪽이면 「09.01~」 · 「~09.07」.
 * 두 날의 해가 다르면 해를 붙인다(「25.12.20~26.01.10」).
 */
export function campaignPeriodFilterLabel(filter: CampaignPeriodFilter | null | undefined): string {
  const start = filter?.start ?? null;
  const end = filter?.end ?? null;
  if (!start && !end) return '전체';
  const withYear = Boolean(start && end && start.getFullYear() !== end.getFullYear());
  const fmt = (d: Date | null) => (d ? formatDot(d, withYear) : '');
  return `${fmt(start)}~${fmt(end)}`;
}

/**
 * 게시글의 시각 원값 → ms. PostTable 이 날짜 칸을 그릴 때와 같은 규칙이다:
 * 8자리(YYYYMMDD) 숫자·문자열은 로컬 날짜, 그 밖의 숫자는 epoch(초면 ms 로), 문자열·Date 는 `new Date`.
 * @returns 못 읽으면 `null`
 */
export function normalizePostTimestampMs(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') {
    const abs = Math.abs(raw);
    if (abs >= 10_000_000 && abs <= 99_999_999) {
      const d = new Date(Math.floor(raw / 10_000), Math.floor((abs % 10_000) / 100) - 1, abs % 100);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
    return abs < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (/^\d{8}$/.test(s)) {
      const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
    if (/^\d+$/.test(s)) return normalizePostTimestampMs(Number(s));
  }
  const d = raw instanceof Date ? raw : new Date(raw as string);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** 게시글의 게시 시각(ms) — `timestamp` → `postDate` → `date` 순으로 본다(표의 날짜 칸과 같다) */
export function postTimestampMs(post: { timestamp?: unknown; postDate?: unknown; date?: unknown } | null | undefined): number | null {
  return normalizePostTimestampMs(post?.timestamp ?? post?.postDate ?? post?.date);
}

function startOfDayMs(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}
function endOfDayMs(d: Date): number {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
}

/**
 * 게시글이 필터 구간 안에 있는가. 필터가 비어 있으면 늘 true.
 * 게시일을 못 읽는 글은 (필터가 걸려 있으면) false.
 */
export function postInPeriod(
  post: { timestamp?: unknown; postDate?: unknown; date?: unknown } | null | undefined,
  filter: CampaignPeriodFilter | null | undefined
): boolean {
  if (!isCampaignPeriodFilterActive(filter)) return true;
  const t = postTimestampMs(post);
  if (t === null) return false;
  if (filter?.start && t < startOfDayMs(filter.start)) return false;
  if (filter?.end && t > endOfDayMs(filter.end)) return false;
  return true;
}

function formatDot(d: Date, withYear: boolean): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return withYear ? `${String(d.getFullYear()).slice(2)}.${mm}.${dd}` : `${mm}.${dd}`;
}
